/* ============================================================================
   DRIVE IQ - ASK IQ V1.0
   Standalone voice-first co-driver intelligence module.
   PURPOSE
   - Capture natural voice/text requests.
   - Preserve full user intent.
   - Hand search requests to the Drive IQ search/provider layer.
   - Rank returned places for the driver's stated intent.
   - Speak ONE strong recommendation first.
   - Handle natural follow-ups: yes, no, next, another, top three, what are the others.
   - Require explicit route/add-stop confirmation before any route handoff.
   - Never own or modify map camera, guidance, reroute state, or navigation state.
   HTML IDS EXPECTED LATER
   - diqAskBtn
   - diqAskSheet
   - diqAskClose
   - diqAskStatus
   - diqAskInput
   - diqAskGo
   SEARCH HANDOFF
   Listen for:
     window event: "driveiq:ask-search"
   Event detail:
     {
       requestId,
       query,
       intent,
       context,
       timestamp
     }
   RETURN SEARCH RESULTS
   Call:
     window.DRIVE_IQ_ASK.receiveResults(results, meta)
   EXPLICIT ROUTE HANDOFF
   Listen for:
     window event: "driveiq:ask-route-confirmed"
   Or install:
     window.DRIVE_IQ_ASK.setRouteHandler(function(place, meta){ ... });
   \\\\============================================================================ */
(function(window,document){
  "use strict";
  if(window.DRIVE_IQ_ASK && window.DRIVE_IQ_ASK.__version){
    return;
  }
  var VERSION="1.13.0-v35-voice-followup-expansion";
  var autoCloseTimer=null;
  var searchClearButton=null;
  var recognition=null;
  var speaking=false;
  var activeUtterance=null;
  var listening=false;
  var currentRequest=null;
  var currentResults=[];
  var currentIndex=0;
  var awaitingRouteConfirmation=false;
  var awaitingRecommendationAcceptance=false;
  var awaitingSearchExpansion=false;
  var expansionPromptSerial=0;
  var routeHandler=null;
  var pendingRouteHandoff=null;
  var searchHandler=null;
  var requestCounter=0;
  // V34: ephemeral conversation exclusions, never route authority.
  var excludedStopIds={};
  var maxAcceptedMiles=null;
  var conversationBaseQuery="";
  var conversationCategory="";
  var followUpSearches=0;
  function stopIdentity(place){
    return lower(place&&(place.googlePlaceId||place.placeId||place.id||place.name)||"");
  }
  function validRestroomCandidate(place){
    var name=lower(placeName(place));
    var types=lower([place&&place.primaryType,place&&place.category,(place&&place.types||[]).join(" ")].join(" "));
    var combined=name+" "+types;
    if(/\b(urgent care|medical|clinic|hospital|auto sales|car dealer|bail bonds|gym|fitness|attorney|lawyer)\b/.test(combined)){return false;}
    if(/^(?:bathroom|restroom|toilet)$/.test(name) && !/public_bathroom|public_restroom|rest_stop/.test(types)){return false;}
    return /public_bathroom|public_restroom|rest_stop|rest_area|visitor_center|travel_center|truck_stop|gas_station|convenience_store|supermarket|grocery_store/.test(types) ||
      /\b(welcome center|visitor center|rest area|travel stop|travel center|truck stop|loves|love's|pilot|flying j|exxon|shell|kroger|walmart)\b/.test(name);
  }
  function acceptableFollowupCandidate(place,intent){
    if(excludedStopIds[stopIdentity(place)]){return false;}
    if(intent&&intent.category==="restroom"&&!validRestroomCandidate(place)){return false;}
    var miles=milesFromPlace(place);
    if(maxAcceptedMiles!==null && (miles===null || miles>=maxAcceptedMiles)){return false;}
    return true;
  }
  function markRejected(place){
    if(place){excludedStopIds[stopIdentity(place)]=true;}
  }
  // A search-expansion question is NOT a route confirmation. Keep these states separate.
  function askSearchExpansion(message){
    awaitingRouteConfirmation=false;
    awaitingRecommendationAcceptance=false;
    awaitingSearchExpansion=true;
    var serial=++expansionPromptSerial;
    var listenWhenFinished=function(){
      window.setTimeout(function(){
        var sheet=byId("diqAskSheet");
        if(serial!==expansionPromptSerial||!awaitingSearchExpansion||
           !sheet||!sheet.classList.contains("on")||listening||speaking){return;}
        startListening();
      },300);
    };
    if(!speak(message,{reason:"search-expansion-question",onFinished:listenWhenFinished})){
      // Even when speech synthesis is unavailable, preserve the typed answer path.
      listenWhenFinished();
    }
    return true;
  }
  function answerSearchExpansion(agreed){
    awaitingSearchExpansion=false;
    ++expansionPromptSerial;
    if(!agreed){
      speak("Okay, I won't look farther. Tell me if you'd like to search again.",
        {reason:"search-expansion-declined"});
      return true;
    }
    // The user approved a WIDER SEARCH, not a navigation handoff.
    // Keep rejected stops excluded, but relax the previous distance ceiling.
    maxAcceptedMiles=null;
    followUpSearches=0;
    var base=conversationBaseQuery||"Find a public restroom";
    // Broaden the original local request to include stops farther along the route.
    var broader=base.replace(/\b(near me|nearby|around me|closest|closer|within [0-9.]+ miles?)\b/gi," ")
      .replace(/\s+/g," ").trim();
    if(!/\b(on my route|along my route|ahead|on the way)\b/i.test(broader)){
      broader+=" along my route";
    }
    runSearch(broader,"approved-restroom-expansion",true);
    return true;
  }
  function requestCloserOrNext(tooFar){
    var previous=currentPlace();
    markRejected(previous);
    if(tooFar){
      var m=milesFromPlace(previous);
      if(m!==null){maxAcceptedMiles=maxAcceptedMiles===null?m:Math.min(m,maxAcceptedMiles);}
    }
    awaitingRouteConfirmation=false;
    awaitingRecommendationAcceptance=false;
    for(var i=currentIndex+1;i<currentResults.length;i++){
      if(acceptableFollowupCandidate(currentResults[i],currentRequest&&currentRequest.intent)){
        currentIndex=i;return presentCurrentRecommendation();
      }
    }
    if(followUpSearches<1 && conversationBaseQuery){
      followUpSearches++;
      // Refresh provider results using the ORIGINAL request, never raw "too far".
      runSearch(conversationBaseQuery,"validated-follow-up",true);
      return true;
    }
    if(tooFar){
      askSearchExpansion("I couldn't verify a suitable restroom closer than that. Want me to look farther ahead?");
    }else{
      speak("I don't have another suitable option from this search.",{reason:"validated-options-exhausted"});
    }
    return true;
  }
  // V1.8.1 — false no-results race protection.
  // Empty async responses are provisional; valid returned places always win.
  var noResultsTimer=null;
  var noResultsDelayMs=3500;
  var acceptedResultsRequestId="";
  function clearNoResultsTimer(){
    if(noResultsTimer){
      try{window.clearTimeout(noResultsTimer);}catch(error){}
      noResultsTimer=null;
    }
  }
  function scheduleNoResults(requestId){
    clearNoResultsTimer();
    noResultsTimer=window.setTimeout(function(){
      noResultsTimer=null;
      if(
        !currentRequest ||
        String(currentRequest.requestId)!==String(requestId)
      ){
        return;
      }
      if(currentResults.length){
        return;
      }
      awaitingRecommendationAcceptance=false;
      awaitingRouteConfirmation=false;
      if(followUpSearches>0 && conversationCategory==="restroom"){
        askSearchExpansion("I couldn't verify another suitable restroom within that distance. Want to try farther ahead?");
      }else{
        speak("I could not find a good match for that. Try asking a different way.",{reason:"no-results"});
      }
    },noResultsDelayMs);
  }
  var DEFAULT_CONTEXT={
    mode:"auto",
    activeRoute:false,
    destination:null,
    vehicle:null,
    heading:null
  };
  var contextProvider=function(){
    return Object.assign({},DEFAULT_CONTEXT);
  };
  function byId(id){
    return document.getElementById(id);
  }
  function clean(value){
    return String(value==null?"":value).replace(/\s+/g," ").trim();
  }
  function numberValue(value){
    var n=Number(value);
    return isFinite(n)?n:null;
  }
  function lower(value){
    return clean(value).toLowerCase();
  }
  function dispatch(name,detail){
    try{
      window.dispatchEvent(new CustomEvent(name,{detail:detail||{}}));
    }catch(error){}
  }
  function setStatus(message){
    var node=byId("diqAskStatus");
    if(node){
      node.textContent=String(message||"");
    }
    dispatch("driveiq:ask-status",{message:String(message||"")});
  }
  function setListening(value){
    var sheet=byId("diqAskSheet");
    var button=byId("diqAskBtn");
    listening=!!value;
    if(sheet){
      sheet.classList.toggle("listening",listening);
    }
    if(button){
      button.classList.toggle("listening",listening);
      button.setAttribute("aria-pressed",listening?"true":"false");
    }
    dispatch("driveiq:ask-listening",{listening:listening});
  }
  function setSheetOpen(value){
    var sheet=byId("diqAskSheet");
    if(sheet){
      sheet.classList.toggle("on",!!value);
      sheet.setAttribute("aria-hidden",value?"false":"true");
    }
    dispatch(value?"driveiq:ask-open":"driveiq:ask-close",{});
  }
  function stopVoiceCapture(){
    if(recognition){
      try{recognition.stop();}catch(error){}
    }
    recognition=null;
    setListening(false);
  }
  function stopSpeaking(){
    speaking=false;
    try{
      if(window.speechSynthesis){
        window.speechSynthesis.cancel();
      }
    }catch(error){}
    activeUtterance=null;
  }
  function closeAsk(){
    if(autoCloseTimer){window.clearTimeout(autoCloseTimer);autoCloseTimer=null;}
    stopVoiceCapture();
    stopSpeaking();
    awaitingSearchExpansion=false;
    ++expansionPromptSerial;
    setSheetOpen(false);
    return true;
  }
  function openAsk(options){
    var settings=options||{};
    if(autoCloseTimer){window.clearTimeout(autoCloseTimer);autoCloseTimer=null;}
    setSheetOpen(true);
    if(settings.listen!==false){
      window.setTimeout(startListening,80);
    }
    return true;
  }
  function speechRecognitionCtor(){
    return window.SpeechRecognition||window.webkitSpeechRecognition||null;
  }
  function speechSynthesisSupported(){
    return !!(
      window.speechSynthesis &&
      typeof window.SpeechSynthesisUtterance==="function"
    );
  }
  function chooseVoice(){
    var voices=[];
    if(!speechSynthesisSupported()){
      return null;
    }
    try{
      voices=window.speechSynthesis.getVoices()||[];
    }catch(error){
      voices=[];
    }
    if(!voices.length){
      return null;
    }
    var preferred=voices.filter(function(voice){
      var lang=lower(voice.lang);
      var name=lower(voice.name);
      return (
        /^en-us/.test(lang) &&
        /samantha|ava|allison|susan|serena|premium|enhanced|natural/.test(name)
      );
    });
    if(preferred.length){
      return preferred[0];
    }
    var english=voices.filter(function(voice){
      return /^en/.test(lower(voice.lang));
    });
    return english[0]||voices[0]||null;
  }
  function speak(text,options){
    var message=clean(text);
    var settings=options||{};
    var voice;
    if(!message){
      return false;
    }
    setStatus(message);
    dispatch("driveiq:ask-spoken",{
      text:message,
      reason:clean(settings.reason||"response")
    });
    if(!speechSynthesisSupported()){
      return false;
    }
    stopSpeaking();
    try{
      activeUtterance=new SpeechSynthesisUtterance(message);
      voice=chooseVoice();
      if(voice){
        activeUtterance.voice=voice;
      }
      activeUtterance.rate=0.93;
      activeUtterance.pitch=1.0;
      activeUtterance.volume=1.0;
      activeUtterance.onstart=function(){
        speaking=true;
      };
      activeUtterance.onend=function(){
        speaking=false;
        activeUtterance=null;
        if(typeof settings.onFinished==="function"){
          try{settings.onFinished();}catch(finishError){}
        }
        if(
          awaitingRecommendationAcceptance ||
          awaitingRouteConfirmation
        ){
          window.setTimeout(function(){
            if(
              !speaking &&
              !listening &&
              (
                awaitingRecommendationAcceptance ||
                awaitingRouteConfirmation
              )
            ){
              startListening();
            }
          },220);
        }
      };
      activeUtterance.onerror=function(){
        speaking=false;
        activeUtterance=null;
        if(typeof settings.onFinished==="function"){
          try{settings.onFinished();}catch(finishError){}
        }
      };
      window.speechSynthesis.speak(activeUtterance);
      return true;
    }catch(error){
      speaking=false;
      activeUtterance=null;
      return false;
    }
  }
  function startListening(){
    var Ctor=speechRecognitionCtor();
    var input=byId("diqAskInput");
    // A fresh listening session replaces the previous transcription.
    if(input){input.value=""; updateClearButton();}
    if(!Ctor){
      setStatus("Voice search is not available here. Type your request below.");
      if(input){
        try{input.focus();}catch(error){}
      }
      return false;
    }
    stopVoiceCapture();
    stopSpeaking();
    try{
      recognition=new Ctor();
      recognition.lang="en-US";
      recognition.continuous=false;
      recognition.interimResults=true;
      recognition.maxAlternatives=1;
      recognition.onstart=function(){
        setListening(true);
        setStatus("Listening...");
      };
      recognition.onresult=function(event){
        var transcript="";
        var finalText="";
        var i;
        for(i=event.resultIndex;i<event.results.length;i+=1){
          if(event.results[i]&&event.results[i][0]){
            transcript+=clean(event.results[i][0].transcript)+" ";
            if(event.results[i].isFinal){
              finalText+=clean(event.results[i][0].transcript)+" ";
            }
          }
        }
        transcript=clean(transcript);
        finalText=clean(finalText);
        if(transcript){
          setStatus(transcript);
          if(input){
            input.value=transcript;
          updateClearButton();
          }
        }
        if(finalText){
          stopVoiceCapture();
          handleUserInput(finalText,"voice");
        }
      };
      recognition.onerror=function(event){
        var type=event&&event.error?String(event.error):"";
        setListening(false);
        if(type==="not-allowed"||type==="service-not-allowed"){
          setStatus("Microphone permission is off. Type your request below.");
        }else if(type==="no-speech"){
          setStatus("I did not catch that. Tap ASK and try again.");
        }else{
          setStatus("Voice search paused. Tap ASK to try again.");
        }
      };
      recognition.onend=function(){
        setListening(false);
      };
      recognition.start();
      return true;
    }catch(error){
      setListening(false);
      setStatus("Voice search is unavailable right now. Type your request below.");
      return false;
    }
  }
  function milesFromPlace(place){
    var direct=numberValue(
      place&&(
        place.distanceMiles!=null
          ?place.distanceMiles
          :place.distance_miles
      )
    );
    if(direct!==null){
      return Math.max(0,direct);
    }
    var meters=numberValue(
      place&&(
        place.distanceMeters!=null
          ?place.distanceMeters
          :place.distance_meters
      )
    );
    if(meters!==null){
      return Math.max(0,meters/1609.344);
    }
    return null;
  }
  function detourMinutes(place){
    var values=[
      place&&place.detourMinutes,
      place&&place.detour_minutes,
      place&&place.extraMinutes,
      place&&place.addedMinutes,
      place&&place.routeDetourMinutes,
      place&&place.detour&&place.detour.minutes
    ];
    var i;
    var n;
    for(i=0;i<values.length;i+=1){
      n=numberValue(values[i]);
      if(n!==null){
        return Math.max(0,n);
      }
    }
    return null;
  }
  function ratingValue(place){
    return numberValue(
      place&&(
        place.rating!=null
          ?place.rating
          :place.googleRating!=null
            ?place.googleRating
            :place.averageRating
      )
    );
  }
  function reviewCount(place){
    var values=[
      place&&place.reviewCount,
      place&&place.ratingCount,
      place&&place.userRatingsTotal,
      place&&place.googleRatingCount
    ];
    var i;
    var n;
    for(i=0;i<values.length;i+=1){
      n=numberValue(values[i]);
      if(n!==null){
        return Math.max(0,n);
      }
    }
    return null;
  }
  function placeName(place){
    return clean(
      place&&(
        place.name||
        place.label||
        place.title||
        place.displayName
      )||
      "this place"
    );
  }
  function placeText(place){
    return lower([
      placeName(place),
      place&&place.category,
      place&&place.categoryLabel,
      place&&place.primaryCategory,
      place&&place.description,
      place&&place.summary,
      place&&place.tags&&place.tags.join?place.tags.join(" "):"",
      place&&place.types&&place.types.join?place.types.join(" "):""
    ].join(" "));
  }
  function isChain(place){
    var text=placeText(place);
    if(place&&place.isChain===true){return true;}
    if(place&&place.isChain===false){return false;}
    return /starbucks|mcdonald|chick[- ]?fil[- ]?a|taco bell|wendy|burger king|subway|olive garden|applebee|chili|panera|dunkin/.test(text);
  }
  function isLocal(place){
    var text=placeText(place);
    if(place&&place.isLocal===true){return true;}
    if(place&&place.isChain===false){return true;}
    return /local favorite|locally owned|family owned|family-owned|independent|neighborhood favorite/.test(text);
  }
  function parseIntent(query){
    var q=lower(query);
    var intent={
      raw:clean(query),
      category:"",
      namedPlace:"",
      preference:"balanced",
      locationScope:"auto",
      wantsMultiple:false,
      requestedCount:1,
      localBias:false,
      quickBias:false,
      qualityBias:false,
      proximityBias:false,
      destinationBias:false,
      routeBias:false
    };
    if(/\b(top three|top 3|three options|three choices)\b/.test(q)){
      intent.wantsMultiple=true;
      intent.requestedCount=3;
    }else if(/\b(top two|top 2|two options|two choices)\b/.test(q)){
      intent.wantsMultiple=true;
      intent.requestedCount=2;
    }
    if(/\b(best|highest rated|best rated|really good|great)\b/.test(q)){
      intent.preference="best";
      intent.qualityBias=true;
    }
    if(/\b(quick|fast|fastest|closest|easy stop|least detour|not far|without taking me far|on the way)\b/.test(q)){
      intent.preference="quick";
      intent.quickBias=true;
      intent.routeBias=true;
    }
    if(/\b(local|locally owned|independent|not a chain|no chains)\b/.test(q)){
      intent.preference="local";
      intent.localBias=true;
    }
    if(/\b(near my destination|at my destination|by my destination|close to my destination)\b/.test(q)){
      intent.locationScope="destination";
      intent.destinationBias=true;
    }else if(/\b(ahead|on my route|along my route|on the way)\b/.test(q)){
      intent.locationScope="route";
      intent.routeBias=true;
    }else if(/\b(near me|nearby|around me|closest)\b/.test(q)){
      intent.locationScope="nearby";
      intent.proximityBias=true;
    }
    if(/\b(mexican|taco|taqueria)\b/.test(q)){intent.category="mexican";}
    else if(/\b(coffee|cafe|espresso|latte)\b/.test(q)){intent.category="coffee";}
    else if(/\b(gas|fuel|gasoline|fill up)\b/.test(q)){intent.category="gas";}
    else if(/\b(food|eat|restaurant|lunch|dinner|breakfast|hungry|pizza|pizzeria|burger|chicken|taco|burrito|taqueria)\b/.test(q)){intent.category="food";}
    else if(/\b(restroom|bathroom|toilet)\b/.test(q)){intent.category="restroom";}
    else if(/\b(parking|park)\b/.test(q)){intent.category="parking";}
    else if(/\b(grocery|groceries|supermarket)\b/.test(q)){intent.category="grocery";}
    else if(/\b(hotel|motel|lodging)\b/.test(q)){intent.category="hotel";}
    else if(/\b(dessert|ice cream|gelato|donut|sweet)\b/.test(q)){intent.category="dessert";}
    /*
      Preserve named-business intent.
      The old implementation only knew a small hard-coded brand list, which
      meant "Find Pizza Hut near me" was flattened into "pizza restaurant".
      That defeats exact-brand proximity because Google never receives the
      business name the driver actually asked for.
    */
    var knownNames=[
      "chick-fil-a","chick fil a","starbucks","mcdonald's","mcdonalds",
      "taco bell","wendy's","wendys","subway","shell","exxon","chevron",
      "bp","walmart","target","costco","hilton","marriott","hyatt",
      "pizza hut","domino's","dominos","papa john's","papa johns",
      "little caesars","waffle house","whataburger","sonic","arbys","arby's",
      "cracker barrel","olive garden","texas roadhouse","longhorn steakhouse"
    ];
    var i;
    var explicitMatch;
    var explicitName;
    for(i=0;i<knownNames.length;i+=1){
      if(q.indexOf(knownNames[i])>=0){
        intent.namedPlace=knownNames[i];
        break;
      }
    }
    /*
      Also support local/non-chain names without maintaining a brand dictionary:
      "find El Agave near me" -> El Agave
      "find Pizza Hut near me" -> Pizza Hut
      Do NOT turn generic category requests like "find pizza near me" or
      "find Mexican restaurants near me" into fake business names.
    */
    if(!intent.namedPlace){
      explicitMatch=q.match(
        /^(?:please\s+)?(?:find|locate|show me|take me to|look for)\s+(.+?)(?:\s+(?:near me|nearby|around me|ahead|on my route|along my route|on the way))?$/
      );
      if(explicitMatch&&explicitMatch[1]){
        explicitName=clean(explicitMatch[1])
          .replace(/\b(the\s+)?(?:best|closest|nearest|good|great|quick)\b/gi," ")
          .replace(/\s+/g," ")
          .trim();
        if(
          explicitName &&
          !/^(?:a\s+|an\s+|some\s+)?(?:pizza|pizzeria|mexican(?: restaurant)?s?|restaurant(?:s)?|food|coffee|cafe|gas|fuel|gas station|hotel|motel|grocery|grocery store|supermarket|restroom|bathroom|parking|burger(?:s)?|sushi|chinese|italian|seafood|steakhouse|bbq|barbecue|chicken|wings?)$/i.test(explicitName)
        ){
          intent.namedPlace=explicitName;
        }
      }
    }
    return intent;
  }
  function getContext(){
    var value;
    try{
      value=contextProvider();
    }catch(error){
      value=null;
    }
    return Object.assign({},DEFAULT_CONTEXT,value||{});
  }
  function buildSearchRequest(query,source,preserveConversation){
    clearNoResultsTimer();
    acceptedResultsRequestId="";
    awaitingSearchExpansion=false;
    ++expansionPromptSerial;
    if(!preserveConversation){
      excludedStopIds={};maxAcceptedMiles=null;followUpSearches=0;
      conversationBaseQuery=clean(query);
      conversationCategory=parseIntent(query).category;
    }
    requestCounter+=1;
    var request={
      requestId:"ask-"+String(Date.now())+"-"+String(requestCounter),
      query:clean(query),
      source:String(source||"unknown"),
      intent:parseIntent(query),
      context:getContext(),
      timestamp:Date.now()
    };
    currentRequest=request;
    if(autoCloseTimer){window.clearTimeout(autoCloseTimer);autoCloseTimer=null;}
    currentResults=[];
    currentIndex=0;
    awaitingRouteConfirmation=false;
    awaitingRecommendationAcceptance=false;
    return request;
  }
  function runSearch(query,source,preserveConversation){
    var request=buildSearchRequest(query,source,preserveConversation);
    setStatus("Searching along your route...");
    dispatch("driveiq:ask-search",request);
    if(typeof searchHandler==="function"){
      try{
        var result=searchHandler(request);
        if(result&&typeof result.then==="function"){
          result.then(function(payload){
            if(Array.isArray(payload)){
              receiveResults(payload,{requestId:request.requestId});
            }else if(payload&&Array.isArray(payload.results)){
              receiveResults(payload.results,Object.assign({},payload,{
                requestId:request.requestId
              }));
            }
          }).catch(function(){
            receiveResults([],{requestId:request.requestId,error:true});
          });
        }else if(Array.isArray(result)){
          receiveResults(result,{requestId:request.requestId});
        }else if(result&&Array.isArray(result.results)){
          receiveResults(result.results,Object.assign({},result,{
            requestId:request.requestId
          }));
        }
      }catch(error){
        receiveResults([],{requestId:request.requestId,error:true});
      }
    }
    return request;
  }
  function scorePlace(place,intent,index){
    var score=0;
    var rating=ratingValue(place);
    var reviews=reviewCount(place);
    var miles=milesFromPlace(place);
    var detour=detourMinutes(place);
    var local=isLocal(place);
    var chain=isChain(place);
    var text=placeText(place);
    if(rating!==null){
      score+=(rating-3.5)*18;
    }
    if(reviews!==null){
      score+=Math.min(14,Math.log(reviews+1)*1.9);
    }
    if(miles!==null){
      /*
        Interstate rule:
        distance ahead is not the same thing as a bad stop.
        A place 25 miles ahead with a tiny detour can be more practical than
        a place 5 miles away that pulls the driver far off route.
      */
      if(place&&place.isAlongRoute===true){
        score-=Math.min(12,miles*.22);
      }else{
        score-=Math.min(20,miles*.62);
      }
      if(intent.proximityBias){
        if(miles<=1){ score+=34; }
        else if(miles<=2){ score+=25; }
        else if(miles<=3){ score+=16; }
        else if(miles<=5){ score+=5; }
        else{ score-=Math.min(28,(miles-5)*2.4); }
        if(rating!==null && rating<3.8){
          score-=24;
        }
      }
    }
    if(detour!==null){
      score-=Math.min(34,detour*2.8);
    }
    if(place&&place.routeAheadPractical===true){
      score+=10;
    }
    if(intent.localBias){
      if(local){score+=22;}
      if(chain){score-=28;}
    }
    if(intent.quickBias){
      if(detour!==null){score-=detour*3.2;}
      if(miles!==null){score-=miles*.8;}
    }
    if(intent.qualityBias){
      if(rating!==null){score+=(rating-4)*16;}
      if(reviews!==null){score+=Math.min(12,Math.log(reviews+1)*1.5);}
    }
    if(intent.namedPlace){
      if(text.indexOf(lower(intent.namedPlace))>=0){
        score+=120;
      }else{
        score-=60;
      }
    }
    if(intent.category&&text.indexOf(lower(intent.category))>=0){
      score+=10;
    }
    if(place&&place.isAlongRoute===true){score+=16;}
    if(place&&place.nearDestination===true && intent.destinationBias){score+=24;}
    if(place&&place.isOpen===true){score+=5;}
    /*
      Preserve provider/result ordering as a small tie-breaker only.
    */
    score-=Math.min(10,Number(index)||0)*0.35;
    return score;
  }
  function proximityBucket(place){
    var miles=milesFromPlace(place);
    if(miles===null){return 99;}
    if(miles<=0.75){return 0;}
    if(miles<=1.5){return 1;}
    if(miles<=2.5){return 2;}
    if(miles<=4){return 3;}
    if(miles<=6){return 4;}
    if(miles<=10){return 5;}
    return 6;
  }
  function rankResults(results,intent){
    return results
      .map(function(place,index){
        return {
          place:place,
          score:scorePlace(place,intent,index),
          proximity:proximityBucket(place),
          originalIndex:index
        };
      })
      .sort(function(a,b){
        /*
          "Near me" means physical proximity is the first decision gate.
          Quality/reviews/open status decide among places in the same useful
          distance band instead of letting a 4-5 mile result leapfrog a good
          place on the corner.
        */
        if(intent&&intent.proximityBias && a.proximity!==b.proximity){
          return a.proximity-b.proximity;
        }
        if(b.score!==a.score){
          return b.score-a.score;
        }
        return a.originalIndex-b.originalIndex;
      })
      .map(function(entry){
        return entry.place;
      });
  }
  function formatMiles(miles){
    if(miles===null){
      return "";
    }
    if(miles<0.2){
      return "just ahead";
    }
    if(miles<1){
      var closeRounded=Math.max(0.1,Math.round(miles*10)/10);
      return closeRounded+" "+(closeRounded===1?"mile":"miles")+" ahead";
    }
    if(miles<10){
      var rounded=Math.round(miles*10)/10;
      return rounded+" "+(rounded===1?"mile":"miles")+" ahead";
    }
    var whole=Math.round(miles);
    return whole+" "+(whole===1?"mile":"miles")+" ahead";
  }
  function requestSubject(intent){
    var raw=lower(intent&&intent.raw||"");
    if(/\bmexican|taqueria|burrito|taco\b/.test(raw)){return "Mexican restaurant";}
    if(/\bpizza|pizzeria\b/.test(raw)){return "pizza place";}
    if(/\bsushi|japanese\b/.test(raw)){return "sushi restaurant";}
    if(/\bchinese\b/.test(raw)){return "Chinese restaurant";}
    if(/\bitalian\b/.test(raw)){return "Italian restaurant";}
    if(/\bbbq|barbecue\b/.test(raw)){return "barbecue restaurant";}
    if(/\bsteak|steakhouse\b/.test(raw)){return "steakhouse";}
    if(/\bseafood\b/.test(raw)){return "seafood restaurant";}
    if(/\bburger|hamburger\b/.test(raw)){return "burger place";}
    if(/\bchicken|wings|wing\b/.test(raw)){return "chicken place";}
    if(/\bcoffee|cafe|espresso|latte\b/.test(raw)){return "coffee stop";}
    if(/\bgas|fuel|gasoline\b/.test(raw)){return "fuel stop";}
    if(/\bhotel|motel|lodging\b/.test(raw)){return "hotel";}
    if(/\bgrocery|groceries|supermarket\b/.test(raw)){return "grocery stop";}
    if(/\brestroom|bathroom|toilet\b/.test(raw)){return "restroom stop";}
    return "match";
  }
  function recommendationReason(place,intent){
    var rating=ratingValue(place);
    var detour=detourMinutes(place);
    var local=isLocal(place);
    var chain=isChain(place);
    if(intent.localBias && local && !chain){
      return "It is a strong local option.";
    }
    if(intent.quickBias && detour!==null && detour<=2){
      return "It keeps you very close to your route.";
    }
    if(
      intent.proximityBias &&
      rating!==null &&
      rating>=4.2
    ){
      return "It is one of the stronger nearby options.";
    }
    if(intent.qualityBias && rating!==null && rating>=4.6){
      return "It has one of the strongest ratings in the results.";
    }
    if(place&&place.isAlongRoute===true){
      return "It fits your route well.";
    }
    if(rating!==null && rating>=4.5){
      return "It is highly rated.";
    }
    if(local&&!chain){
      return "It is a local pick.";
    }
    return "It is the strongest fit for what you asked for.";
  }
  function recommendationIntro(intent,index){
    if(index>0){
      return "Next pick is";
    }
    if(intent.preference==="local"){
      return "For something local, I would go with";
    }
    if(intent.preference==="quick"){
      return "The easiest good option is";
    }
    if(intent.preference==="best"){
      return "My pick from the matches I found is";
    }
    return "Best fit along your route is";
  }
  function recommendationSpeech(place,intent,index){
    var parts=[];
    var name=placeName(place);
    var routeMiles=numberValue(place&&place.routeAheadMiles);
    var miles=routeMiles!==null&&routeMiles>=0?routeMiles:milesFromPlace(place);
    var detour=detourMinutes(place);
    var subject=requestSubject(intent);
    var distant=(miles!==null && miles>=12);
    /*
      Long-distance interstate behavior:
      if the best valid match is far ahead, say that clearly instead of
      treating distance alone as a search failure.
    */
    if(index===0 && distant){
      parts.push("One "+subject+" I found is "+name+".");
    }else{
      parts.push(recommendationIntro(intent,index)+" "+name+".");
    }
    if(miles!==null){
      parts.push("It is "+(routeMiles!==null&&routeMiles>=0?formatMiles(miles):((Math.round(miles*10)/10)+" miles away"))+".");
    }
    if(detour!==null){
      if(detour<=0.5){
        parts.push("It adds almost no time to your route.");
      }else{
        parts.push("It adds about "+Math.max(1,Math.round(detour))+" minutes to your route.");
      }
    }
    if(distant && place&&place.isAlongRoute===true){
      parts.push("It is farther ahead, but it stays close to your route.");
    }else{
      parts.push(recommendationReason(place,intent));
    }
    parts.push("Want to go there?");
    return clean(parts.join(" "));
  }
  function multiResultSpeech(count){
    var max=Math.min(count,currentResults.length);
    var lines=[];
    var i;
    var place;
    var miles;
    var detour;
    for(i=0;i<max;i+=1){
      place=currentResults[i];
      miles=milesFromPlace(place);
      detour=detourMinutes(place);
      var part=(i+1)+". "+placeName(place);
      if(miles!==null){
        part+=", "+formatMiles(miles);
      }
      if(detour!==null){
        part+=", about "+Math.max(1,Math.round(detour))+" minutes off route";
      }
      lines.push(part+".");
    }
    return clean(
      "I found "+String(max)+" good options. "+
      lines.join(" ")+" Say the name, say next, or say route me to one."
    );
  }
  function presentCurrentRecommendation(){
    if(!currentResults.length){
      speak("I could not find a strong match. Try changing what you asked for.",{
        reason:"no-results"
      });
      return false;
    }
    if(currentIndex>=currentResults.length){
      currentIndex=0;
    }
    awaitingRecommendationAcceptance=true;
    awaitingRouteConfirmation=false;
    return speak(
      recommendationSpeech(
        currentResults[currentIndex],
        currentRequest?currentRequest.intent:parseIntent(""),
        currentIndex
      ),
      {reason:"recommendation"}
    );
  }
  function receiveResults(results,meta){
    var info=meta||{};
    var list=Array.isArray(results)?results.slice():[];
    var requestId;
    if(
      currentRequest &&
      info.requestId &&
      String(info.requestId)!==String(currentRequest.requestId)
    ){
      return false;
    }
    if(!currentRequest){
      currentRequest=buildSearchRequest(
        info.query||"",
        info.source||"external-results"
      );
    }
    requestId=String(
      info.requestId ||
      (currentRequest&&currentRequest.requestId) ||
      ""
    );
    // Empty callbacks are provisional. Do not let them erase a valid result set.
    if(!list.length){
      if(
        currentResults.length &&
        acceptedResultsRequestId===requestId
      ){
        return true;
      }
      scheduleNoResults(requestId);
      return false;
    }
    // V34: apply suitability and conversation exclusions to ALL callbacks,
    // including external/late results, before ASK speaks a recommendation.
    list=list.filter(function(place){return acceptableFollowupCandidate(place,currentRequest.intent);});
    if(!list.length){
      scheduleNoResults(requestId);
      return false;
    }
    clearNoResultsTimer();
    currentResults=rankResults(list,currentRequest.intent);
    currentIndex=0;
    acceptedResultsRequestId=requestId;
    dispatch("driveiq:ask-results",{
      request:currentRequest,
      results:currentResults.slice(),
      count:currentResults.length
    });
    if(!currentResults.length){
      scheduleNoResults(requestId);
      return false;
    }
    if(currentRequest.intent.wantsMultiple){
      speak(
        multiResultSpeech(currentRequest.intent.requestedCount),
        {reason:"multiple-results"}
      );
      return true;
    }
    return presentCurrentRecommendation();
  }
  function nextRecommendation(){
    if(!currentResults.length){
      speak("I do not have another result yet.",{reason:"no-next-result"});
      return false;
    }
    if(currentRequest&&currentRequest.intent&&currentRequest.intent.category==="restroom"){
      return requestCloserOrNext(false);
    }
    if(currentIndex+1>=currentResults.length){
      speak("I have no more matches from this search. You can clear the box and try a wider search.",{
        reason:"end-of-results"
      });
      return false;
    }
    currentIndex+=1;
    return presentCurrentRecommendation();
  }
  // V15.5D: explicit user-selected recommendation; does not change navigation.
  function selectRecommendationAt(index){
    var i=Number(index);
    if(!Number.isInteger(i)||i<0||i>=currentResults.length){return false;}
    if(pendingRouteHandoff||awaitingRouteConfirmation){return false;}
    currentIndex=i;
    return presentCurrentRecommendation();
  }
  function currentPlace(){
    if(!currentResults.length){
      return null;
    }
    return currentResults[Math.max(0,Math.min(currentIndex,currentResults.length-1))]||null;
  }
  function askForRouteConfirmation(){
    var place=currentPlace();
    if(!place){
      return false;
    }
    awaitingRecommendationAcceptance=false;
    awaitingRouteConfirmation=true;
    speak("Add "+placeName(place)+" as a stop?",{
      reason:"route-confirmation"
    });
    dispatch("driveiq:ask-route-confirmation-requested",{
      place:place,
      request:currentRequest
    });
    return true;
  }
  /* Stop addition is asynchronous. Emitting a request is NOT proof of a new route. */
  function confirmRoute(){
    var place=currentPlace();
    if(!place){return false;}
    if(pendingRouteHandoff){
      setStatus("Still waiting for navigation to confirm that stop.");
      return true;
    }
    if(!awaitingRouteConfirmation){return askForRouteConfirmation();}
    awaitingRouteConfirmation=false;
    awaitingRecommendationAcceptance=false;
    var meta={source:"ask-iq",request:currentRequest,explicitConfirmation:true,
      timestamp:Date.now(),handoffId:"ask-stop-"+Date.now()};
    pendingRouteHandoff={place:place,meta:meta};
    window.setTimeout(function(){
      if(pendingRouteHandoff&&pendingRouteHandoff.meta.handoffId===meta.handoffId){
        pendingRouteHandoff=null;
        setStatus("Stop request sent, but navigation has not confirmed it. Check your route.");
      }
    },20000);
    speak("Okay. I'll request "+placeName(place)+" as a stop.",{reason:"route-requested"});
    // Preserve the established Find Stops event handoff; do not install routes here.
    dispatch("driveiq:ask-route-confirmed",{place:place,meta:meta});
    if(typeof routeHandler==="function"){
      try{routeHandler(place,meta);}catch(error){
        dispatch("driveiq:ask-route-handoff-error",{meta:meta,error:String(error)});
      }
    }
    return true;
  }
  /* Called only when the route authority verifies the updated route. */
  function scheduleVerifiedClose(delay){
    if(autoCloseTimer){window.clearTimeout(autoCloseTimer);}
    autoCloseTimer=window.setTimeout(function(){
      autoCloseTimer=null;
      var sheet=byId("diqAskSheet");
      if(sheet && sheet.classList.contains("on")){closeAsk();}
    },delay);
  }
  function resolveRouteHandoff(success,meta){
    if(!pendingRouteHandoff){return false;}
    if(meta&&meta.handoffId&&meta.handoffId!==pendingRouteHandoff.meta.handoffId){return false;}
    var place=pendingRouteHandoff.place;
    pendingRouteHandoff=null;
    if(!success){
      speak("Navigation did not confirm that stop. Please check your route.",
        {reason:"route-unconfirmed"});
      return true;
    }
    // Route authority has confirmed installation. Allow spoken feedback to finish.
    if(autoCloseTimer){window.clearTimeout(autoCloseTimer);autoCloseTimer=null;}
    var completed=false;
    var onFinished=function(){
      if(completed){return;}
      completed=true;
      scheduleVerifiedClose(450);
    };
    var speechStarted=speak(placeName(place)+" is now on your route.",
      {reason:"route-installed",onFinished:onFinished});
    if(!speechStarted){
      onFinished();
    }else{
      // Failsafe for engines that omit SpeechSynthesisUtterance.onend.
      // Do not close quickly enough to truncate the normal confirmation.
      scheduleVerifiedClose(7500);
    }
    return true;
  }
  function cancelRouteConfirmation(){
    awaitingRouteConfirmation=false;
    speak("Okay. I will keep your current route.",{
      reason:"route-cancelled"
    });
    dispatch("driveiq:ask-route-cancelled",{
      place:currentPlace(),
      request:currentRequest
    });
    return true;
  }
  function handleFollowUp(query){
    var q=lower(query);
    var wantsOther=/\b(next|another|another one|give me another|something else|show me another|what else|other options|not that one)\b/.test(q);
    var saysNo=/^(?:no|nope|nah|cancel|never mind|nevermind|stay on route|no thanks|no thank you)[.! ]*$/.test(q);
    var tooFar=/\b(too far|closer|not so far|nearer|too many miles|less distance)\b/.test(q);
    var saysYes=/^(?:yes|yeah|yep|yup|sure|okay|ok|do it|confirm|sounds good|that works|i like that|go with that|yes please|sure thing|go ahead|look farther|search farther|try farther)(?:[.! ]*)$/.test(q);
    if(awaitingSearchExpansion){
      if(saysYes || /\b(yes|yeah|sure|okay|ok)\b.*\b(farther|further|ahead|search|look)\b/.test(q)){
        return answerSearchExpansion(true);
      }
      if(/^(?:no|nope|nah|no thanks|no thank you|cancel|never mind|nevermind)[.! ]*$/.test(q)){
        return answerSearchExpansion(false);
      }
      // A different request cancels the pending question; do not route by accident.
      awaitingSearchExpansion=false;
      ++expansionPromptSerial;
      return false;
    }
    if(tooFar && currentRequest && currentRequest.intent.category==="restroom" && currentResults.length){
      return requestCloserOrNext(true);
    }
    if(pendingRouteHandoff){
      if(saysNo){speak("The stop request was already sent. Check your route before making another change.",{reason:"pending-handoff"});return true;}
      if(saysYes){setStatus("Waiting for navigation to confirm the stop.");return true;}
    }
    if(awaitingRouteConfirmation){
      if(saysNo){return cancelRouteConfirmation();}
      if(wantsOther){awaitingRouteConfirmation=false;return nextRecommendation();}
      if(saysYes){return confirmRoute();}
    }
    if(/\b(top three|top 3|three options|what are the others|read the others)\b/.test(q)){
      if(!currentResults.length){return false;}
      awaitingRouteConfirmation=false;
      awaitingRecommendationAcceptance=true;
      return speak(multiResultSpeech(3),{reason:"multiple-results-follow-up"});
    }
    if(wantsOther || (awaitingRecommendationAcceptance&&saysNo)){
      awaitingRecommendationAcceptance=false;
      return nextRecommendation();
    }
    if(/\b(route me there|route there|take me there|navigate there|add it|add that stop|add this stop|let's go there|go there)\b/.test(q)){
      return askForRouteConfirmation();
    }
    if(awaitingRecommendationAcceptance&&saysYes){return askForRouteConfirmation();}
    return false;
  }
  function handleUserInput(value,source){
    var query=clean(value);
    var input=byId("diqAskInput");
    if(!query){
      return false;
    }
    if(input){
      input.value=query;
      updateClearButton();
    }
    stopVoiceCapture();
    stopSpeaking();
    if(
      (currentResults.length || awaitingSearchExpansion) &&
      (
        awaitingSearchExpansion ||
        awaitingRecommendationAcceptance ||
        awaitingRouteConfirmation ||
        /\b(next|another|too far|closer|nearer|top three|top 3|what are the others|route me there|take me there|navigate there)\b/i.test(query)
      )
    ){
      if(handleFollowUp(query)){
        dispatch("driveiq:ask-follow-up",{
          query:query,
          source:source||"unknown",
          currentIndex:currentIndex,
          place:currentPlace()
        });
        return true;
      }
    }
    if(pendingRouteHandoff){
      setStatus("Waiting for navigation to confirm the requested stop.");
      return true;
    }
    runSearch(query,source||"unknown");
    return true;
  }
  function updateClearButton(){
    var input=byId("diqAskInput");
    var btn=byId("diqAskClear");
    if(!btn||!input){return;}
    var hasText=!!clean(input.value);
    btn.style.display=hasText?"flex":"none";
    btn.setAttribute("aria-hidden",hasText?"false":"true");
  }
  function clearAskInput(){
    stopVoiceCapture();
    stopSpeaking();
    clearNoResultsTimer();
    // Clear ASK conversation only; never cancel or alter the active trip.
    currentRequest=null;
    currentResults=[];
    currentIndex=0;
    acceptedResultsRequestId="";
    awaitingRecommendationAcceptance=false;
    awaitingRouteConfirmation=false;
    var input=byId("diqAskInput");
    if(input){input.value="";input.placeholder="Ask Drive IQ anything...";}
    updateClearButton();
    setStatus(pendingRouteHandoff?"Navigation is confirming your stop.":"Ready for another question.");
    return true;
  }
  function ensureClearButton(){
    var input=byId("diqAskInput");
    if(!input){return;}
    var wrapper=byId("diqAskInputWrap");
    if(!wrapper){
      var originalParent=input.parentNode;
      if(!originalParent){return;}
      wrapper=document.createElement("div");
      wrapper.id="diqAskInputWrap";
      wrapper.style.position="relative";
      wrapper.style.flex="1 1 0%";
      wrapper.style.minWidth="0";
      wrapper.style.display="flex";
      wrapper.style.alignItems="stretch";
      originalParent.insertBefore(wrapper,input);
      wrapper.appendChild(input);
    }
    input.style.flex="1 1 auto";
    input.style.minWidth="0";
    input.style.width="100%";
    input.style.boxSizing="border-box";
    input.style.paddingRight="48px";
    var btn=byId("diqAskClear");
    if(!btn){
      btn=document.createElement("button");
      btn.type="button";
      btn.id="diqAskClear";
      btn.setAttribute("aria-label","Clear ASK search");
      btn.setAttribute("title","Clear search");
      btn.textContent="\u00d7";
      wrapper.appendChild(btn);
    }else if(btn.parentNode!==wrapper){
      wrapper.appendChild(btn);
    }
    if(!btn.__diqAskBound){
      btn.__diqAskBound=true;
      btn.addEventListener("click",function(event){
        if(event){event.preventDefault();event.stopPropagation();}
        clearAskInput();
      },false);
    }
    if(!input.__diqAskClearInputBound){
      input.__diqAskClearInputBound=true;
      input.addEventListener("input",updateClearButton,false);
    }
    updateClearButton();
  }
  function bindUI(){
    ensureClearButton();
    var button=byId("diqAskBtn");
    var close=byId("diqAskClose");
    var go=byId("diqAskGo");
    var input=byId("diqAskInput");
    if(button && !button.__diqAskBound){
      button.__diqAskBound=true;
      button.addEventListener("click",function(){
        if(listening){
          stopVoiceCapture();
        }else{
          openAsk({listen:true});
        }
      },false);
    }
    if(close && !close.__diqAskBound){
      close.__diqAskBound=true;
      close.addEventListener("click",closeAsk,false);
    }
    if(go && !go.__diqAskBound){
      go.__diqAskBound=true;
      go.addEventListener("click",function(){
        handleUserInput(input?input.value:"","text");
      },false);
    }
    if(input && !input.__diqAskBound){
      input.__diqAskBound=true;
      input.addEventListener("keydown",function(event){
        if(event && event.key==="Enter"){
          event.preventDefault();
          handleUserInput(input.value,"text");
        }
      },false);
    }
    return true;
  }
  function injectAskStyles(){
    if(document.getElementById("diqAskRuntimeStyle")){return;}
    var style=document.createElement("style");
    style.id="diqAskRuntimeStyle";
    style.textContent=
      "#diqAskStatus{color:#f7fbff!important;font-size:15px!important;line-height:1.42!important;font-weight:800!important;}" +
      "#diqAskInput{color:#fff!important;-webkit-text-fill-color:#fff!important;}" +
      "#diqAskInput::placeholder{color:rgba(255,255,255,.38)!important;}" +
    "#diqAskInputWrap{position:relative!important;flex:1 1 0%!important;min-width:0!important;display:flex!important;align-items:stretch!important;}#diqAskInputWrap #diqAskInput{width:100%!important;min-width:0!important;box-sizing:border-box!important;padding-right:48px!important;}#diqAskInputWrap #diqAskClear{position:absolute!important;right:9px!important;top:50%!important;transform:translateY(-50%)!important;z-index:2!important;width:32px!important;height:32px!important;display:none;align-items:center;justify-content:center;border:0!important;border-radius:50%!important;background:rgba(255,255,255,.13)!important;color:#fff!important;font-size:23px!important;line-height:1!important;padding:0!important;cursor:pointer!important;box-shadow:none!important;}" +
      "#diqAskSheet.listening #diqAskStatus{color:#b6ffeb!important;}" +
      "#diqAskBtn.listening{box-shadow:0 0 0 2px rgba(40,255,190,.42),0 0 28px rgba(40,255,190,.28)!important;}";
    document.head.appendChild(style);
  }
  function init(){
    injectAskStyles();
    bindUI();
    window.setTimeout(bindUI,250);
    window.setTimeout(bindUI,1000);
    return true;
  }
  window.DRIVE_IQ_ASK={
    __version:VERSION,
    init:init,
    open:openAsk,
    close:closeAsk,
    listen:startListening,
    stopListening:stopVoiceCapture,
    stopSpeaking:stopSpeaking,
    speak:speak,
    submit:function(query){
      return handleUserInput(query,"api");
    },
    receiveResults:receiveResults,
    next:nextRecommendation,
    selectIndex:selectRecommendationAt,
    clearSearch:clearAskInput,
    topThree:function(){
      if(!currentResults.length){return false;}
      return speak(multiResultSpeech(3),{reason:"api-top-three"});
    },
    routeCurrent:askForRouteConfirmation,
    confirmRoute:confirmRoute,
    cancelRoute:cancelRouteConfirmation,
    resolveRouteHandoff:resolveRouteHandoff,
    currentPlace:currentPlace,
    currentRequest:function(){
      return currentRequest;
    },
    currentResults:function(){
      return currentResults.slice();
    },
    parseIntent:parseIntent,
    rankResults:function(results,intent){
      return rankResults(
        Array.isArray(results)?results:[],
        intent||parseIntent("")
      );
    },
    setSearchHandler:function(handler){
      searchHandler=typeof handler==="function"?handler:null;
      return !!searchHandler;
    },
    setRouteHandler:function(handler){
      routeHandler=typeof handler==="function"?handler:null;
      return !!routeHandler;
    },
    setContextProvider:function(provider){
      contextProvider=typeof provider==="function"
        ?provider
        :function(){return Object.assign({},DEFAULT_CONTEXT);};
      return true;
    },
    status:function(){
      return {
        version:VERSION,
        listening:listening,
        speaking:speaking,
        request:currentRequest,
        resultCount:currentResults.length,
        currentIndex:currentIndex,
        currentPlace:currentPlace(),
        awaitingRecommendationAcceptance:awaitingRecommendationAcceptance,
        awaitingSearchExpansion:awaitingSearchExpansion,
        awaitingRouteConfirmation:awaitingRouteConfirmation,
      pendingRouteHandoff:!!pendingRouteHandoff
      };
    }
  };
  if(document.readyState==="loading"){
    document.addEventListener("DOMContentLoaded",init,{once:true});
  }else{
    init();
  }
})(window,document);
